/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import {
  STATUS_ACTION_REQUIRED,
  STATUS_CHECKED,
  STATUS_COMPLETED,
  STATUS_CREATED,
  STATUS_DATA_COMPLETED,
  STATUS_DATA_COMPLETED_LAWFIRM,
  STATUS_EVALUATE_JUDGEMENT,
  STATUS_EXTRAJUDICIAL_COMMUNICATION_SENT,
  STATUS_EXTRAJUDICIAL_REACTION_RECEIVED,
  STATUS_IN_PROGRESS,
  STATUS_LAWSUIT,
  STATUS_LAWSUIT_COMPLETED,
  STATUS_PREPARE_LAWSUIT,
  STATUS_TRIAL,
  STATUS_WAITING_FOR_FEEDBACK
} from "./leadStatus";
import { APPLICATION_STATUS_SUMMARY } from "./applicationStatus";
import Button from "@mui/material/Button";
import React from "react";
import { getCaseLink, getCaseSendMessageLink, getProductData } from "../productService";
import _get from "lodash/get";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { ExtraContent } from "./StatusBox/ExtraContent";
import { isFlexServiceProduct } from "../advocardService";
import dayjs from "dayjs";
import { Link } from "react-router";

export const getContent = (litigationCase: AbstractCase) => {
  const appointmentStatus = _get(litigationCase, "advisoryAppointment.status");
  const appointmentDate = _get(litigationCase, "advisoryAppointment.appointment.date") && dayjs(_get(litigationCase, "advisoryAppointment.appointment.date"));

  switch (litigationCase.caseCategory) {
    case "advisory":
      switch (litigationCase.leadStatus) {
        case STATUS_CREATED:
          return {
            header: "Fall schildern und Vorgehen festlegen",
            text: "Sie entscheiden, ob Sie nur eine kurze Beratung benötigen, oder wir direkt für Sie tätig werden sollen - zum Beispiel durch ein Schreiben an die Gegenseite. Sie wünschen eine rechtliche Vertretung durch uns? Dann können Sie hier auch direkt Ihre Vollmacht unterzeichnen.",
            buttonTarget: getCaseLink(litigationCase) + "/antrag",
            buttonLabel: "Weiteres Vorgehen festlegen",
          };
        case STATUS_DATA_COMPLETED:
        case STATUS_WAITING_FOR_FEEDBACK:
          return {
            header: "Jetzt Termin vereinbaren",
            text: "Vielen Dank für die Schilderung Ihres Falls. Sie können jetzt einen Termin für eine Erstberatung vereinbaren",
            buttonTarget: getCaseLink(litigationCase) + litigationCase.calendlyLink,
            buttonLabel: "Termin vereinbaren",
          };
        case STATUS_CHECKED:
          return {
            header: "Jetzt Termin vereinbaren",
            text: "Vielen Dank für die Schilderung Ihres Falls. Sie können jetzt einen Termin für eine Erstberatung vereinbaren",
            buttonTarget: getCaseLink(litigationCase) + litigationCase.calendlyLink,
            buttonLabel: "Termin vereinbaren",
          };
        case STATUS_IN_PROGRESS:
          if (appointmentStatus === "not_processed") {
            return {
              header: "Ihr Termin konnte nicht stattfinden",
              text: `Sie hatten einen Termin für ein Erstgespräch am ${
                appointmentDate && appointmentDate.format("DD.MM.YYYY")
              } gebucht. Der Termin konnte nicht stattfinden. Buchen Sie sich gerne einen neuen Termin.`,
              buttonTarget: getCaseLink(litigationCase) + litigationCase.calendlyLink,
              buttonLabel: "Neuen Termin buchen",
            };
          }
          return {
            header: "Termin erfolgreich gebucht",
            text:
              `Sie haben einen Termin für ein Erstgespräch am ${appointmentDate && appointmentDate.format("DD.MM.YYYY")} gebucht.` +
              ` Ihr Rechtsanwalt wird Sie um ${appointmentDate && appointmentDate.format("HH:mm")} Uhr anrufen.`,
          };
        case STATUS_COMPLETED:
          if (isFlexServiceProduct(litigationCase)) {
            if (litigationCase.finalClaimStatusClient === null) {
              return {
                header: "Erstberatung abgeschlossen",
                text: "Vielen Dank für Ihr Vertrauen und dafür, dass wir Sie unterstützen durften! Sie sind der Meinung, dass nach Ihrer Erstberatung noch weitere rechtliche Schritte notwendig sind? Dann klicken Sie bitte hier auf “Weitere Tätigkeit notwendig”",
                buttonTarget: getCaseLink(litigationCase) + "/flex/rueckmeldung",
                buttonLabel: "Weitere Tätigkeit notwendig",
              };
            } else {
              return {
                header: "Erstberatung abgeschlossen",
                text: "Vielen Dank für Ihr Vertrauen und dafür, dass wir Sie unterstützen durften!",
              };
            }
          } else {
            return {
              header: "Fall aktuell abgeschlossen – bei Bedarf jederzeit weitere Hilfe!",
              text: "Vielen Dank für Ihr Vertrauen und dafür, dass wir Sie unterstützen durften! Sie sind der Meinung, dass ihr Fall nicht abgeschlossen ist oder es sind im Nachgang doch noch Probleme aufgetreten? Dann schreiben Sie uns gerne jederzeit eine Nachricht und wir werden selbstverständlich umgehend für Sie tätig!",
              buttonLabel: "Nachricht senden",
              buttonTarget: getCaseSendMessageLink(litigationCase),
              variant: "text",
            };
          }
        default:
          return {
            header: "Technischer Fehler",
            text: "Leider kam es scheinbar zu einem technischen Fehler in Ihrem Fall. Bitte melden Sie sich per E-Mail an service@legalbird.de, damit wir Ihnen zeitnah weiterhelfen können.",
          };
      }
    default:
      switch (litigationCase.leadStatus) {
        case STATUS_CREATED:
          if (litigationCase.applicationStatus > APPLICATION_STATUS_SUMMARY) {
            return {
              header: "Ihre Vollmacht wird benötigt",
              text: "Um Ihren Fall zu prüfen und Ihr Anliegen als Ihr gesetzlicher Vertreter durchsetzen zu können, benötigen wir noch Ihre Vollmacht.",
              buttonTarget: getCaseLink(litigationCase) + "/antrag",
              buttonLabel: "Zur Vollmacht",
            };
          }
          return {
            header: "Fall schildern und Vorgehen festlegen",
            text: "Sie entscheiden, ob Sie nur eine kurze Beratung benötigen, oder wir direkt für Sie tätig werden sollen - zum Beispiel durch ein Schreiben an die Gegenseite. Sie wünschen eine rechtliche Vertretung durch uns? Dann können Sie hier auch direkt Ihre Vollmacht unterzeichnen.",
            buttonTarget: getCaseLink(litigationCase) + "/antrag",
            buttonLabel: "Weiteres Vorgehen festlegen",
          };
        case STATUS_DATA_COMPLETED:
        case STATUS_WAITING_FOR_FEEDBACK:
          return {
            header: "Ihre Angaben sind vollständig",
            text: "Vielen Dank für die Schilderung Ihres Falls. Wir prüfen nun Ihren Fall und leiten zeitnah weitere Schritte für Sie ein!",
          };
        case STATUS_DATA_COMPLETED_LAWFIRM:
          return {
            header: "Ihre Angaben sind vollständig",
            text: "Vielen Dank für die Schilderung Ihres Falls. Wir prüfen nun Ihren Fall und leiten zeitnah weitere Schritte für Sie ein!",
          };
        case STATUS_CHECKED:
          return {
            header: "Ihr Fall wurde geprüft",
            text: "Wir haben Ihren Fall geprüft und leiten jetzt weitere Schritte ein. Durch Sie ist nun zunächst nichts weiter zu veranlassen. Sie werden selbstverständlich immer direkt informiert, wenn es Neuigkeiten zu Ihrem Fall gibt!",
          };
        case STATUS_IN_PROGRESS:
          return {
            header: "Ihr Fall wird bearbeitet",
            text: "Wir setzen uns dafür ein, dass Ihr Anliegen zeitnah und professionell geklärt wird. Sie werden jederzeit von uns über Neuigkeiten informiert.",
          };
        case STATUS_EXTRAJUDICIAL_COMMUNICATION_SENT:
          return {
            header: "Schreiben an die Gegenseite",
            text:
              "Wir haben ein Schreiben an die Gegenseite geschickt und informieren Sie umgehend, sobald die Gegenseite " +
              "auf das Schreiben reagiert. Sollte keine Reaktion stattfinden, kommen wir auf Sie zu, um Ihre Möglichkeiten" +
              " zu besprechen.",
          };
        case STATUS_EXTRAJUDICIAL_REACTION_RECEIVED:
        case STATUS_ACTION_REQUIRED:
          if (!!litigationCase.finalClaimStatusClient) {
            return {
              header: litigationCase.extrajudicialReaction === "noReaction" ? "Keine Reaktion der Gegenseite" : "Die Reaktion der Gegenseite liegt vor",
              text: "Wir haben Ihre Rückmeldung erhalten und werden Ihren Fall entsprechend weiter bearbeiten. Sie werden über das weitere Vorgehen wie gewohnt per Nachricht und Ihre Servicewelt informiert.",
            };
          }
          if (litigationCase.extrajudicialReaction === "noReaction") {
            return {
              header: "Keine Reaktion der Gegenseite",
              text: "Wir haben leider keine Reaktion der Gegenseite erhalten. Bitte teilen Sie uns mit, wie Sie weiter vorgehen möchten.",
              buttonTarget: getCaseLink(litigationCase) + "/rueckmeldung",
              buttonLabel: "Vorgehen festlegen",
            };
          }

          if (litigationCase.extrajudicialReaction) {
            return {
              header: "Die Reaktion der Gegenseite liegt vor",
              text: "Bitte prüfen Sie hier die Reaktion der Gegenseite und teilen Sie uns mit, wie Sie weiter vorgehen möchten.",
              buttonTarget: getCaseLink(litigationCase) + "/rueckmeldung",
              buttonLabel: "Vorgehen festlegen",
            };
          }

          return {
            header: "Wir bereiten die nächsten Schritte vor",
            text: "Wenn es Neuigkeiten gibt oder wir Informationen von Ihnen benötigen, informieren wir Sie umgehend.",
          };

        case STATUS_PREPARE_LAWSUIT:
          return {
            header: "Ihre Klage wird vorbereitet",
            text: "Ihrerseits ist derzeit nichts weiter zu tun. Wir melden uns bei Neuigkeiten und Klärungsbedarf direkt bei Ihnen.",
          };
        case STATUS_LAWSUIT:
          return {
            header: "Gerichtsverfahren läuft",
            text: "Ihr Fall befindet sich im gerichtlichen Verfahren. Wir kümmern uns um alles Weitere und melden uns bei Neuigkeiten und Klärungsbedarf immer direkt bei Ihnen.",
          };
        case STATUS_TRIAL:
          return litigationCase.courtAppointment?.courtDate?.appointmentDateTime
            ? {
                header: "Ihr Gerichtstermin steht fest",
                text: "Das Gericht hat den Termin für Ihr Verfahren festgesetzt. Weitere Informationen können Sie dem Schreiben entnehmen, das Sie in Ihren Dokumenten finden. Melden Sie sich bei Fragen gerne direkt bei uns!",
              }
            : {
                header: "Warten auf Gerichtstermin",
                text: "Es wird auf ein Ladungsschreiben für Ihren Gerichtstermin gewartet. Sobald dieses eingeht, werden Sie es hier in der Servicewelt finden können.",
              };
        case STATUS_EVALUATE_JUDGEMENT:
          return litigationCase.nextInstanceCaseBaseData
            ? {
                header: "Berufungsverfahren läuft",
                text: "Sie haben sich dazu entschieden, Rechtsmittel gegen das Urteil einzulegen. Den eigenständigen Fall dazu finden Sie in unter “Meine Fälle” in Ihrer Servicewelt.",
              }
            : {
                header: "Urteilsprüfung",
                text: "Das Urteil in Ihrem Fall liegt vor. Unsere Experten prüfen derzeit sorgfältig die Sinnhaftigkeit und die Erfolgsaussichten einer Berufung.",
              };
        case STATUS_LAWSUIT_COMPLETED:
          return {
            header: "Der Rechtsstreit wurde beendet",
            text: litigationCase.nextInstanceCaseBaseData
              ? "Ihr Rechtsfall ist in dieser Instanz beendet. Der Rechtsstreit wird in der nächsthöheren Instanz fortgesetzt. Sie finden dazu einen eigenständigen Fall in Ihrer Servicewelt."
              : "Ihr Rechtsfall wurde im gerichtlichen Verfahren entschieden. Wenn keine Partei das Ergebnis anfechtet, ist es rechtskräftig und Sie und die Gegenpartei müssen der Entscheidung des Gerichts Folge leisten.",
          };
        case STATUS_COMPLETED:
          return {
            header: "Fall aktuell abgeschlossen – bei Bedarf jederzeit weitere Hilfe!",
            text: "Vielen Dank für Ihr Vertrauen und dafür, dass wir Sie unterstützen durften! Sie sind der Meinung, dass ihr Fall nicht abgeschlossen ist oder es sind im Nachgang doch noch Probleme aufgetreten? Dann schreiben Sie uns gerne jederzeit eine Nachricht und wir werden selbstverständlich umgehend für Sie tätig!",
            buttonLabel: "Nachricht senden",
            buttonTarget: getCaseSendMessageLink(litigationCase),
            variant: "text",
          };
        default:
          return {
            header: "Technischer Fehler",
            text: "Leider kam es scheinbar zu einem technischen Fehler in Ihrem Fall. Bitte melden Sie sich per E-Mail an service@legalbird.de, damit wir Ihnen zeitnah weiterhelfen können.",
          };
      }
  }
};

export const getButton = (litigationCase: AbstractCase) => {
  const content: Record<string, any> = getContent(litigationCase);
  if (!content.buttonLabel) {
    return null;
  }
  return (
    <Button
      component={Link}
      to={content?.linkType === "external" ? { pathname: content.buttonTarget } : content.buttonTarget}
      target={content?.linkType === "external" ? "_blank" : undefined}
      variant={content?.variant ? content.variant : "contained"}
      color="primary"
      fullWidth
    >
      {content.buttonLabel}
    </Button>
  );
};

export const getPercentage = (litigationCase: AbstractCase) => {
  switch (litigationCase.leadStatus) {
    case STATUS_CREATED:
      return 25;
    case STATUS_DATA_COMPLETED:
    case STATUS_WAITING_FOR_FEEDBACK:
    case STATUS_DATA_COMPLETED_LAWFIRM:
    case STATUS_CHECKED:
      return 50;
    case STATUS_IN_PROGRESS:
    case STATUS_EXTRAJUDICIAL_COMMUNICATION_SENT:
    case STATUS_EXTRAJUDICIAL_REACTION_RECEIVED:
    case STATUS_ACTION_REQUIRED:
    case STATUS_PREPARE_LAWSUIT:
    case STATUS_LAWSUIT:
    case STATUS_TRIAL:
      return 75;
    case STATUS_EVALUATE_JUDGEMENT:
      return 80;
    case STATUS_LAWSUIT_COMPLETED:
      return 90;
    case STATUS_COMPLETED:
      return 100;
    default:
      return 0;
  }
};

export const getStatusBoxConfigLitigationCase = (litigationCase: AbstractCase) => {
  const content = getContent(litigationCase);
  return {
    header: content.header,
    text: content.text,
    ctaButton: getButton(litigationCase),
    productName: getProductData(litigationCase.name, "label"),
    percentage: getPercentage(litigationCase),
    extraContent: <ExtraContent litigationCase={litigationCase} />,
  };
};
